import { Alert, Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField, Typography, useTheme } from '@mui/material';
import { ChangeEvent, FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from './../../../../hooks/api/useNswagClient';
import { PrimaryCategory2, Store, WasteReportRequest } from './../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport, formatDateTimeForDisplay } from './../../../../utils';
import { UserContext } from './../../../../components/shared/useUser';
import dayjs from 'dayjs';
import useShiftConfiguration from '../../../../hooks/configurations/useShiftConfiguration';

type IProps = {
  onClose?: () => void;
}

const WasteReportForm = (props: IProps) => {
  const { selectedStore, user } = useContext(UserContext);
  const [allCategories, setAllCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { getPrimaryCategoriesForStore, runWasteReport } = useNswagClient();
  const { t } = useTranslation('common');
  const theme = useTheme();

  const { calculateOperationalReportsRange } = useShiftConfiguration();
  const [operationalStart, setOperationalStart] = useState<Date | null>(null);
  const [operationalEnd, setOperationalEnd] = useState<Date | null>(null);
  const [isShiftEnabled, setIsShiftEnabled] = useState(true);

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      loadCategories(selectedStore.storeNumber);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const selectedAllStores =
      checkedAllStores || (selectedStores.length > 0 && selectedStores.length === user?.stores?.length);

    const store = selectedAllStores
      ? user?.stores?.[0]
      : selectedStores?.[0];

    if (!store?.storeNumber) return;

    const systemFranchises = user?.franchises?.filter(f =>
      user?.stores?.some(s => s.franchiseId === f.franchiseId),
    ) || [];

    const matchingFranchise = systemFranchises.find(f =>
      f.franchiseId === store?.franchiseId);

    calculateOperationalReportsRange(
      new Date(fromDate),
      new Date(toDate),
      store.storeNumber,
      matchingFranchise?.franchiseName ?? '',
    ).then(({ start, end, isShiftEnabled }) => {
      setIsShiftEnabled(isShiftEnabled);
      setOperationalStart(start);
      setOperationalEnd(end);
    });

  }, [fromDate, toDate, selectedStores, checkedAllStores, user?.stores]);

  const loadCategories = (storeNumber: string) => {
    getPrimaryCategoriesForStore(storeNumber).then((res) => {
      if (res.data) {
        setAllCategories(res.data);
        setSelectedCategories(res.data);
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedStores([]);

    setCheckedAllStores(checked);
  };

  const handleSelectAllCategoriesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    if (checked) {
      setSelectedCategories(allCategories);
    } else {
      setSelectedCategories([]);
    }
  };

  const getMaxDate = () => {
    const today = dayjs().format('YYYY-MM-DD');
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleFromDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleCategorySelectChange = (event: SyntheticEvent, value: PrimaryCategory2[]) => {
    setSelectedCategories(value);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const from = dayjs(fromDate).startOf('day').toDate();
    const to = dayjs(toDate).endOf('day').toDate();
    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);

    const request: WasteReportRequest = {
      reportOnAllAvailableStores: selectedAllStores,
      primaryCategoryIds: selectedCategories.length === allCategories.length ? [] : selectedCategories.map(x => x.id as number),
      stores: selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string),
      from: formatDateForReport(from),
      to: formatDateForReport(to),
    };

    runWasteReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof (error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      {fromDate && toDate && operationalStart && operationalEnd && isShiftEnabled && (
        <Box
          sx={{
            mt: 2,
            display: 'inline-block',
            px: 2,
            py: 2,
            mb: 5,
            border: `1px solid ${theme.palette.secondary[300]}`,
            borderRadius: '8px',
          }}
        >
          <Typography
          >
            {t('shiftingConfiguration.filterPeriodApplied', {
              operationalStart: formatDateTimeForDisplay(operationalStart),
              operationalEnd: formatDateTimeForDisplay(operationalEnd),
            })}
          </Typography>
        </Box>
      )}
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={user?.stores ?? []}
              value={selectedStores}
              onChange={handleSelectChange}
              disabled={checkedAllStores}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedStores.length === 0 && !checkedAllStores}
                  label={t('reporting.select-stores')}
                  placeholder={t('reporting.select-stores')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllStores}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-stores')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={allCategories}
              value={selectedCategories}
              onChange={handleCategorySelectChange}
              getOptionKey={(option) => option.id ?? ''}
              getOptionLabel={(option) => option.primaryCategoryName ?? ''}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedCategories.length === 0}
                  label={t('reporting.select-categories')}
                  placeholder={t('reporting.select-categories')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox checked={allCategories.length === selectedCategories.length}
                onChange={handleSelectAllCategoriesChange} />}
              label={t('reporting.all-categories')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.start-date')}
            type="date"
            required
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: getMinDate(), max: toDate != '' ? toDate : getMaxDate() } }}
            fullWidth
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.end-date')}
            type="date"
            required
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: fromDate != '' ? fromDate : getMinDate(), max: getMaxDate() } }}
            fullWidth
          />
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage &&
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default WasteReportForm;